import React, { useCallback, type ReactNode } from "react";
import {
  TextInput,
  Textarea,
  List,
  ListItem,
  Checkbox,
  Group,
  PasswordInput,
  Box,
  NumberInput,
  NativeSelect,
  Button,
  SegmentedControl,
  ColorPicker,
  Text,
  Switch,
  type SwitchProps,
  type CheckboxProps,
  type TextInputProps,
  type TextareaProps,
  type NativeSelectProps,
  type SegmentedControlProps,
  type ColorPickerProps,
  type PasswordInputProps,
} from "@mantine/core";

import {
  createFormHook,
  createFormHookContexts,
  type StandardSchemaV1Issue,
} from "@tanstack/react-form";

import PhoneInput, {
  type Props as PhoneInputProps,
} from "react-phone-number-input/input";

const { fieldContext, formContext, useFieldContext } = createFormHookContexts();

type PotentialError = StandardSchemaV1Issue | string | undefined;

const ErrorMessage: React.FC<{ err: PotentialError }> = (props) => {
  const { err } = props;
  if (!err) {
    return null;
  }

  return (
    <ListItem c="red">{typeof err === "string" ? err : err.message}</ListItem>
  );
};

const ErrorsList: React.FC<{ errors: PotentialError[] }> = ({ errors }) => {
  const onlyRealErrors = errors.filter((e) => e !== undefined);

  if (onlyRealErrors.length < 1) {
    return null;
  }

  return (
    <List>
      {onlyRealErrors.map((err, idx) => (
        <ErrorMessage key={idx} err={err} />
      ))}
    </List>
  );
};

//
//
//

interface MyTextInputProps extends Omit<TextInputProps, "value" | "onChange"> {
  /**
   * call .trim() on input before calling onChange
   * @default false
   */
  trim?: boolean;
  errors: Array<PotentialError>;
}

export const MyTextInput: React.FC<MyTextInputProps> = (props) => {
  const { errors, trim = false, ...rest } = props;
  const field = useFieldContext<string>();

  const transformInput = useCallback(
    (val: string): string => {
      return trim ? val.trim() : val;
    },
    [trim],
  );

  return (
    <Box>
      <TextInput
        {...rest}
        value={field.state.value}
        onChange={(e) => field.handleChange(transformInput(e.target.value))}
        onBlur={field.handleBlur}
      />

      <ErrorsList errors={errors} />
    </Box>
  );
};

//
//
//

interface AppPasswordInputProps extends PasswordInputProps {
  errors: Array<PotentialError>;
}

const AppPasswordInput: React.FC<AppPasswordInputProps> = (props) => {
  const { errors, ...rest } = props;
  const field = useFieldContext<string>();

  return (
    <Box>
      <PasswordInput
        {...rest}
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
      />

      <ErrorsList errors={errors} />
    </Box>
  );
};

//
//
//

interface AppTextareaProps extends Omit<TextareaProps, "value" | "onChange"> {
  errors: Array<PotentialError>;
}

const AppTextAreaInput: React.FC<AppTextareaProps> = (props) => {
  const { errors, ...rest } = props;
  const field = useFieldContext<string>();

  return (
    <Box mt="md">
      <Textarea
        {...rest}
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        autosize
        minRows={3}
      />

      <ErrorsList errors={errors} />
    </Box>
  );
};

//
//
//

interface TypedSegmentedControlProps<T extends string>
  extends Omit<SegmentedControlProps, "onChange"> {
  label: string;
  data: T[];
  value?: T;
  defaultValue?: T;
  onChange: (value: T) => void;
}

function TypedSegmentedControl<T extends string>(
  props: TypedSegmentedControlProps<T>,
): ReactNode {
  const { onChange, label, ...restOfProps } = props;

  return (
    <Group>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <SegmentedControl
        size="sm"
        {...restOfProps}
        onChange={(untypedVal) => {
          // @ts-expect-error needed to hack typescript here due to following error messagea
          // 'string' is assignable to the constraint of type 'T', but 'T' could be instantiated with a different subtype of constraint 'string'.ts(2430)
          props.onChange(untypedVal);
        }}
      />
    </Group>
  );
}

export interface SelectInputOption<T extends string = string> {
  label: string;
  value: T;
}

//
//
//
interface TypedSelectProps<T extends string>
  extends Omit<NativeSelectProps, "onChange"> {
  data: T[] | Array<SelectInputOption<T>>;
  value?: T;
  defaultValue?: T;
  onChange: (value: T) => void;
  placeholder?: string;
}

function TypedNativeSelect<T extends string>(props: TypedSelectProps<T>) {
  const { data, placeholder, ...restOfProps } = props;
  const field = useFieldContext<T>();

  return (
    <>
      <NativeSelect
        {...restOfProps}
        // data={allopt}
        onChange={(event) => {
          // @ts-expect-error needed to hack typescript here due to following error messagea
          // 'string' is assignable to the constraint of type 'T', but 'T' could be instantiated with a different subtype of constraint 'string'.ts(2430)
          props.onChange(event.currentTarget.value);
        }}
      >
        {placeholder ? <option>{placeholder}</option> : null}
        {data.map((opt, idx) => {
          return (
            <option key={idx} value={typeof opt === "string" ? opt : opt.value}>
              {typeof opt === "string" ? opt : opt.label}
            </option>
          );
        })}
      </NativeSelect>
      <ErrorsList errors={field.state.meta.errors} />
    </>
  );
}

interface AppColorPickerProps extends ColorPickerProps {
  label: string;
}

const AppColorPicker: React.FC<AppColorPickerProps> = (props) => {
  const { label, ...rest } = props;
  return (
    <Box mt="md">
      <Text size="sm" fw={500} mt="xs">
        {label} ({props.value})
      </Text>
      <ColorPicker {...rest} />
    </Box>
  );
};

interface AppSwitchComponentProps
  extends Omit<SwitchProps, "checked" | "onChange"> {}

const AppSwitchComponent: React.FC<AppSwitchComponentProps> = (props) => {
  const field = useFieldContext<boolean>();
  return (
    <Switch
      mt="md"
      {...props}
      checked={field.state.value}
      onChange={(event) => {
        field.handleChange(event.target.checked);
      }}
    />
  );
};

//
//
//
interface AppCheckboxComponentProps
  extends Omit<CheckboxProps, "checked" | "onChange"> {}

const AppCheckboxComponent: React.FC<AppCheckboxComponentProps> = (props) => {
  const field = useFieldContext<boolean>();
  return (
    <Checkbox
      {...props}
      checked={field.state.value}
      onChange={(event) => {
        field.handleChange(event.target.checked);
      }}
    />
  );
};

//
//

function PhoneNumberInput(
  props: Omit<
    PhoneInputProps<TextInputProps>,
    "value" | "onChange" | "inputComponent"
  >,
): React.ReactElement {
  const field = useFieldContext<string>();

  return (
    <>
      <PhoneInput
        {...props}
        inputComponent={TextInput}
        value={field.state.value}
        onChange={(value) => {
          if (value) {
            field.handleChange(value);
          }
        }}
        // TODO: allow different countries lol. default country select was weird in UI
        country="US"
        international
        withCountryCallingCode
        //
        // required
        data-testid="phonenum"
      />
      <ErrorsList errors={field.state.meta.errors} />
    </>
  );
}

export const { useAppForm } = createFormHook({
  fieldComponents: {
    TextField: MyTextInput,
    TextareaField: AppTextAreaInput,
    NumberField: NumberInput,
    SegmentedSelectField: TypedSegmentedControl,
    ColorPickerField: AppColorPicker,
    SwitchField: AppSwitchComponent,
    Checkbox: AppCheckboxComponent,
    Password: AppPasswordInput,
    Select: TypedNativeSelect,
    PhoneNumber: PhoneNumberInput,
  },
  formComponents: {
    SubmitButton: Button.withProps({ type: "submit" }),
  },
  fieldContext,
  formContext,
});
